import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';
import ClientServiceEditModal from './ClientServiceEditModal';

const ClientModal = ({ isOpen, onClose, client, mode, onSuccess, initialTab = 'info' }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    date_of_birth: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clientServices, setClientServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [clientBookings, setClientBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [serviceFilter, setServiceFilter] = useState('all'); // all, active, expired
  const [bookingFilter, setBookingFilter] = useState('all'); // all, current, expired
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    if (client && (mode === 'edit' || mode === 'view')) {
      setFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        password: '',
        date_of_birth: client.date_of_birth || '',
        address: client.address || '',
        emergency_contact: client.emergency_contact || '',
        emergency_phone: client.emergency_phone || '',
        is_active: client.is_active ?? true
      });
      
      // Fetch client services and bookings if viewing or editing
      if (mode === 'view' || mode === 'edit') {
        fetchClientServices();
        fetchClientBookings();
      }

      // Set initial tab
      setActiveTab(initialTab);
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        date_of_birth: '',
        address: '',
        emergency_contact: '',
        emergency_phone: '',
        is_active: true
      });
      setClientServices([]);
      setClientBookings([]);
    }
    setErrors({});
  }, [client, mode, isOpen, initialTab]);

  const fetchClientServices = async () => {
    if (!client?.id) return;

    try {
      setLoadingServices(true);
      const services = await adminAPI.getClientServices({ client_id: client.id });
      setClientServices(Array.isArray(services) ? services : (services.results || []));
    } catch (error) {
      console.error('Error fetching client services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchClientBookings = async () => {
    if (!client?.id) return;

    try {
      setLoadingBookings(true);
      const bookings = await adminAPI.getClientBookings(client.id);
      setClientBookings(Array.isArray(bookings) ? bookings : (bookings.results || []));
    } catch (error) {
      console.error('Error fetching client bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const clientData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        role: 'client',
        is_active: formData.is_active,
        date_of_birth: formData.date_of_birth || null,
        address: formData.address,
        emergency_contact: formData.emergency_contact,
        emergency_phone: formData.emergency_phone
      };

      if (mode === 'create') {
        clientData.password = formData.password;
        await adminAPI.createUser(clientData);
        toast.success('Client créé avec succès');
      } else if (mode === 'edit') {
        if (formData.password) {
          clientData.password = formData.password;
        }
        await adminAPI.updateUser(client.id, clientData);
        toast.success('Client modifié avec succès');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleServiceStatus = async (serviceId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await adminAPI.updateClientService(serviceId, { status: newStatus });
      toast.success(`Service ${newStatus === 'active' ? 'activé' : 'désactivé'} avec succès`);
      fetchClientServices(); // Refresh services
    } catch (error) {
      console.error('Error updating service status:', error);
      toast.error('Erreur lors de la mise à jour du service');
    }
  };

  // Handle edit service
  const handleEditService = (service) => {
    setSelectedService(service);
    setShowEditServiceModal(true);
  };

  // Handle successful service edit
  const handleEditServiceSuccess = () => {
    setShowEditServiceModal(false);
    setSelectedService(null);
    fetchClientServices(); // Refresh services
  };

  // Filter services based on selected filter
  const getFilteredServices = () => {
    if (serviceFilter === 'all') return clientServices;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return clientServices.filter(service => {
      const endDate = new Date(service.end_date);
      endDate.setHours(0, 0, 0, 0);

      // Check if service is truly active (not expired and has remaining sessions)
      const isTrulyActive = service.is_truly_active !== undefined ? service.is_truly_active :
        (service.status === 'active' && endDate >= today &&
         (service.service_type?.type === 'pack' ?
          service.used_sessions < service.total_sessions :
          service.current_month_used < service.service_type?.session_count));

      if (serviceFilter === 'active') {
        return isTrulyActive;
      } else if (serviceFilter === 'expired') {
        return !isTrulyActive;
      }
      return true;
    });
  };

  // Filter bookings based on selected filter
  const getFilteredBookings = () => {
    if (bookingFilter === 'all') return clientBookings;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return clientBookings.filter(booking => {
      const sessionDate = new Date(booking.session?.date);
      sessionDate.setHours(0, 0, 0, 0);

      if (bookingFilter === 'current') {
        return sessionDate >= today;
      } else if (bookingFilter === 'expired') {
        return sessionDate < today;
      }
      return true;
    });
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Nouveau Client' : mode === 'edit' ? 'Modifier Client' : 'Détails Client';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4  ">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[80vh]">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {errors.general}
            </div>
          )}

          {/* Tabs Navigation (only for view/edit modes) */}
          {(mode === 'view' || mode === 'edit') && (
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'info'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <UserIcon className="h-4 w-4 inline mr-2" />
                  Informations
                </button>
                <button
                  onClick={() => setActiveTab('services')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'services'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <CreditCardIcon className="h-4 w-4 inline mr-2" />
                  Services ({clientServices.length})
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'history'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <CalendarDaysIcon className="h-4 w-4 inline mr-2" />
                  Historique ({clientBookings.length})
                </button>
              </nav>
            </div>
          )}

          {/* Tab Content */}
          {(mode === 'create' || activeTab === 'info') && (
            <div className="space-y-6">
            {/* Left Column - Client Information */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" />
                    Informations personnelles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <EnvelopeIcon className="h-5 w-5 mr-2" />
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Password */}
                {!isReadOnly && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {mode === 'create' ? 'Mot de passe *' : 'Nouveau mot de passe (laisser vide pour ne pas changer)'}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={mode === 'create'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}

                {/* Additional Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CalendarDaysIcon className="h-5 w-5 mr-2" />
                    Informations supplémentaires
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adresse
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows={2}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact d'urgence
                        </label>
                        <input
                          type="text"
                          name="emergency_contact"
                          value={formData.emergency_contact}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Téléphone d'urgence
                        </label>
                        <input
                          type="tel"
                          name="emergency_phone"
                          value={formData.emergency_phone}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Client actif</span>
                  </label>
                </div>

                {/* Actions */}
                {!isReadOnly && (
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {loading ? 'Sauvegarde...' : (mode === 'create' ? 'Créer' : 'Modifier')}
                    </button>
                  </div>
                )}
              </form>
            </div>
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Services & Packs
                </h3>
                <div className="flex space-x-2">
                  <select
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1"
                  >
                    <option value="all">Tous les services</option>
                    <option value="active">Services actifs</option>
                    <option value="expired">Services expirés</option>
                  </select>
                </div>
              </div>
              {loadingServices ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Chargement des services...</p>
                </div>
              ) : getFilteredServices().length > 0 ? (
                <div className="space-y-3">
                  {getFilteredServices().map((service) => (
                          <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900">
                                {service.service_type?.name || 'Service'}
                              </h4>
                              <div className="flex items-center space-x-2">
                                {(() => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const endDate = new Date(service.end_date);
                                  endDate.setHours(0, 0, 0, 0);

                                  const isExpired = endDate < today;
                                  const hasNoSessions = service.service_type?.type === 'pack' ?
                                    service.used_sessions >= service.total_sessions :
                                    service.current_month_used >= service.service_type?.session_count;

                                  const isTrulyActive = service.is_truly_active !== undefined ? service.is_truly_active :
                                    (service.status === 'active' && !isExpired && !hasNoSessions);

                                  let statusText = 'Actif';
                                  let statusClass = 'bg-green-100 text-green-800';

                                  if (!isTrulyActive) {
                                    if (isExpired) {
                                      statusText = 'Expiré';
                                      statusClass = 'bg-red-100 text-red-800';
                                    } else if (hasNoSessions) {
                                      statusText = 'Épuisé';
                                      statusClass = 'bg-orange-100 text-orange-800';
                                    } else if (service.status !== 'active') {
                                      statusText = 'Inactif';
                                      statusClass = 'bg-gray-100 text-gray-800';
                                    }
                                  }

                                  return (
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                                      {statusText}
                                    </span>
                                  );
                                })()}
                                {!isReadOnly && (
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleEditService(service)}
                                      className="p-1 text-primary-600 hover:text-primary-900 rounded"
                                      title="Modifier le service"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                    {/* <button
                                      onClick={() => toggleServiceStatus(service.id, service.status)}
                                      className={`px-3 py-1 text-xs font-medium rounded ${
                                        service.status === 'active'
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                                      }`}
                                    >
                                      {service.status === 'active' ? 'Désactiver' : 'Activer'}
                                    </button> */}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>Type: {service.service_type?.type || 'N/A'}</div>
                              {service.service_type?.type === 'pack' ? (
                                <>
                                  <div>Sessions: {service.used_sessions || 0} / {service.total_sessions || 0}</div>
                                  <div>Période: {service.start_date} - {service.end_date}</div>
                                </>
                              ) : (
                                <>
                                  <div>Sessions ce mois: {service.current_month_used || 0} / {service.service_type?.session_count || 0}</div>
                                  <div>Abonnement: {service.start_date} - {service.end_date}</div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCardIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p>
                    {serviceFilter === 'all' ? 'Aucun service trouvé' :
                     serviceFilter === 'active' ? 'Aucun service actif' :
                     'Aucun service expiré'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Historique des réservations
                </h3>
                <div className="flex space-x-2">
                  <select
                    value={bookingFilter}
                    onChange={(e) => setBookingFilter(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1"
                  >
                    <option value="all">Toutes les réservations</option>
                    <option value="current">Réservations à venir</option>
                    <option value="expired">Réservations passées</option>
                  </select>
                </div>
              </div>
              {loadingBookings ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Chargement de l'historique...</p>
                </div>
              ) : getFilteredBookings().length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {getFilteredBookings().map((booking) => (
                          <div key={booking.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {booking.session?.title || 'Séance'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {booking.session?.date} à {booking.session?.start_time?.slice(0, 5)} - {booking.session?.location?.name}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              booking.status === 'confirmed'
                                ? 'bg-green-100 text-green-800'
                                : booking.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.status === 'confirmed' ? 'Confirmé' :
                               booking.status === 'cancelled' ? 'Annulé' : booking.status}
                            </span>
                          </div>
                        ))}
                        {getFilteredBookings().length > 10 && (
                          <div className="text-center text-sm text-gray-500 py-2">
                            ... et {getFilteredBookings().length - 10} autres réservations
                          </div>
                        )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarDaysIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>
                      {bookingFilter === 'all' ? 'Aucune réservation trouvée' :
                       bookingFilter === 'current' ? 'Aucune réservation à venir' :
                       'Aucune réservation passée'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Client Service Edit Modal */}
        {showEditServiceModal && selectedService && (
          <ClientServiceEditModal
            isOpen={showEditServiceModal}
            onClose={() => setShowEditServiceModal(false)}
            onSuccess={handleEditServiceSuccess}
            clientService={selectedService}
          />
        )}
      </div>
  );
};

export default ClientModal;
